
var Model = require("../types/DebouncedModel.js");
var par = require("par");
var debounce = require("debounce");
var escapeRegexp = require("escape-regexp");
var xtend = require("xtend");

module.exports = Model({
	name: "allcontacts",
	init: init,
	uninit: uninit,
	debounce: 100, // Debounce pushes by 50 milliseconds

	_contacts: null,
	_managed: null,
	_visibility: null,
	_raw_contacts: null,

	listeners: listeners,
	triggers: triggers,
	re_display_lists: re_display_lists,
	pass_through: pass_through,
	merged_list: merged_list
});

/**
 * Initializes empty caches for managed users, and contacts
 * Initializes map of which lists are visible (both by default)
 * Initializes empty filter variable
 * Pushes initial state with contacts and managed set to empty arrays
 */
function init() {
	this._contacts = [];
	this._managed = [];

	this._filter = null;

	this.push({
		visibility: {
			contacts: true,
			managed: true
		},
		contacts: [],
		managed: [],
		contacts_raw: [],
		managed_raw: [],
		merged: [],
		removing: null,
	});
}

/**
 * Clears private variables for garbage collection
 */
function uninit() {
	this._contacts = null;
	this._managed = null;
	this._raw_contacts = null;
	this._visibility = null;
	this._filter = null;
}

/**
 * Sets up listeners for changes in contacts and managedusers
 * @return {Object} Map of events to event listeners
 */
function listeners() {
	return {
		"contacts:updated": handle_contacts_update,
		"managedusers:updated": handle_managed_users_update
	};
}

/**
 * Listens on triggers for updating the filter, and toggling lists
 * @return {Object} Map of trigger names to event listeners
 */
function triggers() {
	return {
		"filter": debounce(handle_filter_change.bind(this), 200),
		"toggle:contacts": handle_toggle_visibility.bind(this, "contacts"),
		"toggle:managed": handle_toggle_visibility.bind(this, "managed"),
		"remove:contact:start": handle_trigger_remove_contact_start,
		"remove:contact:cancel": handle_trigger_remove_contact_cancel,
		"remove:contact": handle_trigger_remove_contact,
		"remove:contact:confirm": handle_remove_contact
	};
}

/**
 * Re-displays the current lists with the cached non-filtered version
 */
function re_display_lists() {
	this.pass_through("contacts", this._contacts);
	this.pass_through("managed", this._managed);
}

/**
 * Filters people by a certain regex. Uses their full name for filtering
 * @param  {RegExp} regex  The Regular Expression to filter with
 * @param  {Object} person A person with an fname, lname
 * @return {Boolean}        True if person matches the regex
 */
function filter_by(regex, person) {
	var identity = [person.fname, person.lname].join(" ");
	var matches = identity.match(regex);
	return !!matches;
}

/**
 * Pass a given list of people through the filter process. Then push the updated list
 * @param  {String} name Name of the list to process, either "contacts" or "managed" or "raw_contacts"
 * @param  {Array}  list The array of contacts that was cached
 */
function pass_through(name, list) {
	this["_" + name] = list; // Update the cache for the list

	var filtered = list;

	if (this._filter)
		filtered = list.filter(par(filter_by, this._filter));

	// Create the update object based on the name
	var update_object = {};
	update_object[name] = filtered;
	update_object[name + "_raw"] = list;

	this.merged_list();

	// Push down the update
	this.extend(update_object);
}

/**
 * Toggles the visibility of a list by its name
 * @param  {String} name The name of the list to toggle
 */
function handle_toggle_visibility(name) {
	var update_object = {};
	update_object[name] = !this.data.visibility[name];

	this.extend({
		visibility: xtend(this.data.visibility, update_object)
	});

	this.re_display_lists();

	if (name === "contacts") {
		this.dispatch("toggledcontacts");
	}

	this.emit("usermetrics:trigger:track", `toggle visibility of ${name === "contacts" ? "non-managed" : "managed"} contacts in contacts list`, {
		visible: this.data.visibility[name]
	});
}

/**
 * Re-creates the filter and re-displays the lists based on it
 * @param  {Object} data Should contain the `filter` string. A list of space-delimited words
 * @return {[type]}      [description]
 */
function handle_filter_change(data) {
	var filter = data.filter;

	if (!filter)
		this._filter = null;
	else this._filter = process_filter_text(filter);

	this.re_display_lists();

	this.emit("usermetrics:trigger:track", "filter contacts", {
		filter,
	});
}

/**
 * Creates a regular expression from the filter text.
 * Escapes special characters and ORs the words for matching
 * @param  {String} filter Filter text to turn into a RegExp
 * @return {RegExp}        The resulting regular expression
 */
function process_filter_text(filter) {
	var escaped_text = escapeRegexp(filter);

	var replaced_spaces = escaped_text.replace(/\W+/g, "|");

	return new RegExp(replaced_spaces, "ig");
}

/**
 * Updates the contacts list whenever ContactList updates
 * Before displaying, contacts that are in the managed user list are filtered out
 * @param  {Array} contacts Array of contacts' information
 */
function handle_contacts_update(contacts) {
	this._raw_contacts = contacts;
	var managed = this._managed;
	var unmanaged = filter_existing(managed, contacts);
	this.pass_through("contacts", unmanaged);
	this.extend({
		removing: false,
	});
}

/**
 * Filters out contacts that are in the managed contact list
 * @param  {Array} managed  Array of managed people's information
 * @param  {Array} contacts Array of contacts infromation
 * @return {Array}          Array of contacts that aren't also managed
 */
function filter_existing(managedList, contacts) {
	return contacts?.filter(function (contact) {
		return !managedList?.some(function (managed) {
			return managed._id === contact._id;
		});
	});
}

/**
 * Updates the managed list whenever ManagedUsers updates
 * @param  {Array} managed The list of manged users' information
 */
function handle_managed_users_update(managed) {
	this.pass_through("contacts", filter_existing(managed, this._raw_contacts));
	this.pass_through("managed", managed);
}

function handle_trigger_remove_contact_start(data){
	var who = data.who;
	this.extend({
		removing: who
	});
	this.emit("usermetrics:trigger:track", "open remove contact popup", { who });
}

function handle_trigger_remove_contact(data) {
	var who = data.who;
	this.dispatch("removing", {
		who: who
	});
	this.emit("usermetrics:trigger:track", "open remove contact popup", { who });
}

function handle_trigger_remove_contact_cancel() {
	const current = this.data.removing;
	this.extend({
		removing: false
	});
	this.emit("usermetrics:trigger:track", "cancel remove contact", {who: current});
}

function handle_remove_contact(data) {
	var who = data.who;
	var block = data.block;

	this.emit("contacts:trigger:remove", {
		who: who
	});

	if (block)
		this.emit("blocking:trigger:block", {
			who: who
		});

	this.dispatch("removecontact", {
		who: who
	});
}

function merged_list(){
	var managed = this._managed;
	var contacts = this._contacts;

	this.extend({
		merged: managed.concat(contacts).sort(compare_names)
	});
}

function compare_names(person1, person2) {
	var name1 = (person1.fname || "") + " " + (person1.lname || "");
	var name2 = (person2.fname || "") + " " + (person2.lname || "");

	if (name1 < name2) return -1;
	if (name2 < name1) return 1;
	return 0;
}